import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { trackHeartbeat } from "@/lib/visitor-tracking.functions";
import { getOrCreateVisitorId, getStoredConsent } from "@/components/CookieConsent";
import { captureAttribution } from "@/lib/attribution";

const SESSION_KEY = "axc-session-id";
const SESSION_TTL_MS = 30 * 60_000; // 30 min idle = new session
const HEARTBEAT_INTERVAL_MS = 20_000;

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return crypto.randomUUID();
  const raw = window.sessionStorage.getItem(SESSION_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { id: string; lastSeen: number };
      if (Date.now() - parsed.lastSeen < SESSION_TTL_MS) {
        parsed.lastSeen = Date.now();
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
        return parsed.id;
      }
    } catch {
      /* fall through */
    }
  }
  const id = crypto.randomUUID();
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: Date.now() }));
  return id;
}

function touchSession(id: string) {
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: Date.now() }));
}

export function useVisitorTracking() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const heartbeat = useServerFn(trackHeartbeat);
  const firstHitRef = useRef(true);
  const landingPathRef = useRef<string | null>(null);
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Don't track auth routes (login flows). Admin routes ARE tracked so
    // admins show up on Customer Live Map alongside normal visitors.
    if (
      pathname.startsWith("/login") ||
      pathname.startsWith("/reset-password")
    ) {
      return;
    }

    const send = async () => {
      const consent = getStoredConsent();
      if (!consent || !consent.analytics) return;

      const visitorId = getOrCreateVisitorId();
      const sessionId = getOrCreateSessionId();
      touchSession(sessionId);

      if (!landingPathRef.current) {
        landingPathRef.current = pathname;
      }

      const isFirstHit = firstHitRef.current || lastPathRef.current !== pathname;
      firstHitRef.current = false;
      lastPathRef.current = pathname;

      try {
        await heartbeat({
          data: {
            visitorId,
            sessionId,
            consent: {
              necessary: true,
              analytics: consent.analytics,
              marketing: consent.marketing,
            },
            currentPath: pathname,
            landingPath: landingPathRef.current,
            referrer: document.referrer || undefined,
            isFirstHit,
          },
        });
      } catch (e) {
        // Silent — tracking should never break the app
        console.debug("visitor heartbeat failed", e);
      }
    };

    // Defer initial attribution + heartbeat until the browser is idle so they
    // don't inflate TBT/LCP on the first paint. Lighthouse mobile runs on a
    // throttled CPU — every ms of deferred work directly improves the score.
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    const idleId = schedule(() => {
      captureAttribution();
      void send();
    }, { timeout: 3000 });

    // Then poll every 20s while page is open
    const id = setInterval(send, HEARTBEAT_INTERVAL_MS);

    // Re-send if consent changes mid-session
    const onConsent = () => void send();
    window.addEventListener("axc:consent-changed", onConsent);

    return () => {
      clearInterval(id);
      const cancel = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cancel) cancel(idleId);
      else window.clearTimeout(idleId);
      window.removeEventListener("axc:consent-changed", onConsent);
    };
  }, [pathname, heartbeat]);
}
